import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import BottomNav from './components/BottomNav';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import Services from './pages/Services';
import Orders from './pages/Orders';
import Marketing from './pages/Marketing';
import Users from './pages/Users';
import Stores from './pages/Stores';
import System from './pages/System';
import Logs from './pages/Logs';
import Dietary from './pages/Dietary';
import { updateUser, updateMyProfile, getStores, getSystemSettings } from './api';
import './styles/App.css';

// 主应用程序组件（在Router内部）
function AppContent() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [hasStores, setHasStores] = useState(false);
  const [profileForm, setProfileForm] = useState({ nickname: '', phone: '', password: '', newPassword: '' });
  const [systemName, setSystemName] = useState(sessionStorage.getItem('system_name') || '居家健康管理系统');

  // 更新浏览器标签页标题
  useEffect(() => {
    document.title = systemName;
  }, [systemName]);

  useEffect(() => {
    const savedUser = sessionStorage.getItem('health_user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
    // 加载系统设置
    getSystemSettings().then(res => {
      if (res.data.success && res.data.data.hasOwnProperty('system_name')) {
        setSystemName(res.data.data.system_name || '居家健康管理系统');
      }
    }).catch(() => {});
    setLoading(false);
  }, []);

  // 检查是否有门店（admin登录时）
  useEffect(() => {
    if (user && user.role === 'admin') {
      getStores().then(res => {
        if (res.data && res.data.data) {
          setHasStores(res.data.data.length > 0);
        }
      }).catch(() => {
        setHasStores(false);
      });
    }
  }, [user]);

  const handleLogin = (userData, storeId) => {
    // 管理后台登录自动赋予admin角色
    if (storeId === 'admin') {
      userData.role = 'admin';
      userData.storeId = null;
    } else if (storeId) {
      // 店长和店员都绑定门店ID
      userData.storeId = storeId;
    }
    setUser(userData);
    sessionStorage.setItem('health_user', JSON.stringify(userData));
  };

  const handleLogout = () => {
    setUser(null);
    sessionStorage.removeItem('health_user');
    window.location.href = '/';
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      const data = { username: profileForm.username, nickname: profileForm.nickname, phone: profileForm.phone };
      if (profileForm.newPassword) {
        data.newPassword = profileForm.newPassword;
      }
      let res;
      if (user.role === 'admin') {
        // admin 用原来的更新接口
        res = await updateUser(user.id, data);
      } else {
        // 店长/店员用新的个人资料接口
        res = await updateMyProfile(data);
      }
      if (res.data.success) {
        const updatedUser = { ...user, username: data.username, nickname: data.nickname, phone: data.phone };
        setUser(updatedUser);
        sessionStorage.setItem('health_user', JSON.stringify(updatedUser));
        alert('更新成功');
        setShowProfileModal(false);
        setShowMenu(false);
      } else {
        alert(res.data.message || '更新失败');
      }
    } catch (error) {
      alert('更新失败');
    }
  };

  const openProfileModal = () => {
    setProfileForm({ username: user.username || '', nickname: user.nickname || '', phone: user.phone || '', password: '', newPassword: '' });
    setShowProfileModal(true);
    setShowMenu(false);
  };

  if (loading) {
    return <div className="loading-text">加载中...</div>;
  }

  if (!user) {
    return <Login onLogin={handleLogin} systemName={systemName} />;
  }

  const permissions = user.permissions ? user.permissions.split(',') : [];

  return (
    <div className="app">
      <div className="page-title-bar">
        <span className="page-title">欢迎使用{systemName}</span>
        <div className="header-right">
          <div className="user-menu">
            <span className="admin-name" onClick={() => setShowMenu(!showMenu)} style={{cursor: 'pointer'}}>
              {user.nickname || user.username} ▼
            </span>
            {showMenu && (
              <div className="dropdown-menu">
                <div className="dropdown-item" onClick={openProfileModal}>修改资料</div>
                <div className="dropdown-item" onClick={handleLogout}>退出</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        {/* admin直接显示所有菜单 */}
        {user.role === 'admin' && <Route path="/dashboard" element={<Dashboard systemName={systemName} />} />}
        {user.role === 'admin' && <Route path="/customers" element={<Customers />} />}
        {user.role === 'admin' && <Route path="/services" element={<Services />} />}
        {user.role === 'admin' && <Route path="/dietary" element={<Dietary />} />}
        {user.role === 'admin' && <Route path="/orders" element={<Orders />} />}
        {user.role === 'admin' && <Route path="/marketing" element={<Marketing />} />}
        {/* 非管理员用户(店长/店员)默认有所有权限 */}
        {(user.role !== 'admin') && <Route path="/dashboard" element={<Dashboard systemName={systemName} />} />}
        {(user.role !== 'admin') && <Route path="/customers" element={<Customers />} />}
        {(user.role !== 'admin') && <Route path="/services" element={<Services />} />}
        {(user.role !== 'admin') && <Route path="/dietary" element={<Dietary />} />}
        {(user.role !== 'admin') && <Route path="/orders" element={<Orders />} />}
        {(user.role !== 'admin') && <Route path="/marketing" element={<Marketing />} />}
        {user.role === 'admin' && <Route path="/stores" element={<Stores />} />}
        {user.role === 'admin' && <Route path="/system" element={<System />} />}
        {user.role === 'admin' && <Route path="/logs" element={<Logs />} />}
      </Routes>

      <BottomNav permissions={permissions} isAdmin={user.role === 'admin'} hasStores={hasStores} />

      {showProfileModal && (
        <div className="modal-mask">
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-title">
              <span>修改密码/修改昵称</span>
              <span className="modal-close" onClick={() => setShowProfileModal(false)}>×</span>
            </div>
            <form onSubmit={handleUpdateProfile}>
              <div className="modal-content">
                <div className="form-row">
                  <div className="form-field">
                    <label className="form-label">用户名</label>
                    <input className="form-input" value={profileForm.username} onChange={e => setProfileForm({...profileForm, username: e.target.value})} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-field">
                    <label className="form-label">昵称</label>
                    <input className="form-input" value={profileForm.nickname} onChange={e => setProfileForm({...profileForm, nickname: e.target.value})} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-field">
                    <label className="form-label">电话</label>
                    <input className="form-input" value={profileForm.phone} onChange={e => setProfileForm({...profileForm, phone: e.target.value})} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-field">
                    <label className="form-label">新密码（旧密码留空则不变）</label>
                    <input className="form-input" type="password" value={profileForm.newPassword} onChange={e => setProfileForm({...profileForm, newPassword: e.target.value})} />
                  </div>
                </div>
              </div>
              <div className="modal-foot">
                <button type="button" onClick={() => setShowProfileModal(false)}>取消</button>
                <button type="submit" className="action-btn btn-blue">保存</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// 根组件 - 包含Router
function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;